/**
 * NEW TOOLS to add to FinanceTools.jsx
 *
 * Add to the 'fire' or 'saving' category or create a new 'canada' category:
 *
 * New category to add to CATEGORIES array:
 * {
 *   id: 'canada', label: 'Canada', color: '#e74c3c', icon: '🍁',
 *   desc: 'RRSP, TFSA, SIP, and Canada-specific financial tools',
 *   tools: [
 *     { id:'sip',        label:'SIP Calculator',          icon:'📅', component: SIPCalc        },
 *     { id:'retirement', label:'Retirement Calculator',   icon:'🏖️', component: RetirementCalc  },
 *     { id:'rrsp',       label:'RRSP Contribution',       icon:'🍁', component: RRSPCalc        },
 *     { id:'tfsa',       label:'TFSA Growth Calculator',  icon:'💎', component: TFSACalc        },
 *     { id:'dividend',   label:'Dividend Yield',          icon:'💰', component: DividendCalc    },
 *     { id:'currency',   label:'Currency Converter',      icon:'🌍', component: CurrencyConverter },
 *   ]
 * }
 */

import React, { useState, useEffect,useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';

const fmt = (n, sym='$', d=0) => isNaN(n)||!isFinite(n) ? '—' : `${sym}${Math.abs(n).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d})}`;
const fmtPct = (n,d=1) => isNaN(n)||!isFinite(n) ? '—' : `${n.toFixed(d)}%`;

const INPUT = { width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid rgba(255,255,255,.09)', background:'rgba(255,255,255,.05)', color:'rgba(255,255,255,0.75)', fontSize:13, fontFamily:'monospace', outline:'none' };
const SLIDER = (color) => ({ width:'100%', accentColor:color, cursor:'pointer' });
const shortNum = (n, sym) => {
  if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${sym}${(n / 1_000).toFixed(0)}K`;
  return `${sym}${n}`;
};
function Field({ label, help, children }) {
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
        <label style={{ fontSize:12, fontWeight:600, color:'rgba(255,255,255,0.75)' }}>{label}</label>
        {help && <span style={{ fontSize:11, color:'rgba(255,255,255,0.75)' }}>{help}</span>}
      </div>
      {children}
    </div>
  );
}

function ResultCard({ label, value, color='#00d4ff', large, sub }) {
  return (
    <div style={{ padding:'12px 14px', borderRadius:9, background:`${color}10`, border:`1px solid ${color}30`, textAlign:'center' }}>
      <div style={{ fontSize:11, color:`${color}90`, marginBottom:4, textTransform:'uppercase', letterSpacing:'.08em' }}>{label}</div>
      <div style={{ fontSize:large?22:18, fontWeight:800, color, fontFamily:'monospace' }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'rgba(255,255,255,0.75)', marginTop:3 }}>{sub}</div>}
    </div>
  );
}

function ResultGrid({ children }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:10, marginTop:20, paddingTop:16, borderTop:'1px solid rgba(255,255,255,.07)' }}>
      {children}
    </div>
  );
}

/* ── SIP Calculator (Systematic Investment Plan) ─────────────────── */
export function SIPCalc({ sym='$' }) {
  const [monthly,    setMonthly]    = useState(500);
  const [rate,       setRate]       = useState(12);
  const [years,      setYears]      = useState(20);
  const [lumpsum,    setLumpsum]    = useState(0);

  const r         = rate / 100 / 12;
  const n         = years * 12;
  const sipFV     = monthly * (Math.pow(1+r,n)-1) / r * (1+r);
  const lumpFV    = lumpsum * Math.pow(1+rate/100, years);
  const totalFV   = sipFV + lumpFV;
  const invested  = monthly * n + lumpsum;
  const gains     = totalFV - invested;
const chartData = useMemo(() => {
  return Array.from({ length: years + 1 }, (_, y) => {
    const rY      = rate / 100 / 12;
    const nY      = y * 12;
    const sipFV_  = rY > 0
      ? monthly * (Math.pow(1 + rY, nY) - 1) / rY * (1 + rY)
      : monthly * nY;
    const lumpFV_ = lumpsum * Math.pow(1 + rate / 100, y);
    const total_  = sipFV_ + lumpFV_;
    const contrib = monthly * nY + lumpsum;
    return {
      year:         new Date().getFullYear() + y,
      total:        Math.round(Math.max(0, total_)),
      contributions:Math.round(contrib),
      gains:        Math.round(Math.max(0, total_ - contrib)),
    };
  });
}, [monthly, lumpsum, rate, years]);
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Monthly SIP Amount"><input type="number" value={monthly} min={0} onChange={e=>setMonthly(+e.target.value)} style={INPUT}/></Field>
        <Field label="One-time Lumpsum (optional)"><input type="number" value={lumpsum} min={0} onChange={e=>setLumpsum(+e.target.value)} style={INPUT}/></Field>
        <Field label={`Expected Annual Return: ${rate}%`}><input type="range" min={1} max={30} step={0.5} value={rate} onChange={e=>setRate(+e.target.value)} style={SLIDER('#00d4ff')}/></Field>
        <Field label={`Investment Period: ${years} years`}><input type="range" min={1} max={40} value={years} onChange={e=>setYears(+e.target.value)} style={SLIDER('#00d4ff')}/></Field>
      </div>
      <ResultGrid>
        <ResultCard label="Future Value" value={fmt(totalFV,sym)} color="#00d4ff" large/>
        <ResultCard label="Amount Invested" value={fmt(invested,sym)} color="#4d8fff"/>
        <ResultCard label="Estimated Gains" value={fmt(gains,sym)} color="#2ecc71"/>
        <ResultCard label="Return Multiple" value={`${(totalFV/invested).toFixed(1)}x`} color="#f1c40f"/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:'rgba(220,230,250,.45)',
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    SIP Growth Over Time
  </div>

  <ResponsiveContainer width="100%" height={260}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="year"
        tick={{ fill:'rgba(220,230,250,.45)', fontSize:11 }}
        tickLine={false} axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:'rgba(220,230,250,.45)', fontSize:11 }}
        tickLine={false} axisLine={false}
        width={64}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{ background:'#0c0f18', border:'1px solid rgba(255,255,255,.07)', borderRadius:8, fontSize:12 }}
        labelStyle={{ color:'rgba(220,230,250,.45)', marginBottom:4 }}
        itemStyle={{ color:'#dce6fa' }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'total'         ? 'Total value'        :
          name === 'contributions' ? 'Amount invested'     :
          'Estimated gains',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:'rgba(220,230,250,.45)', paddingTop:8 }}
        formatter={name =>
          name === 'total'         ? 'Total value'    :
          name === 'contributions' ? 'Amount invested' :
          'Estimated gains'
        }
      />

      {/* Contributions — straight growing line */}
      <Line
        type="monotone" dataKey="contributions"
        stroke="#4d8fff" strokeWidth={1.5}
        strokeDasharray="6 4" dot={false} activeDot={false}
      />
      {/* Gains only */}
      <Line
        type="monotone" dataKey="gains"
        stroke="#2ecc71" strokeWidth={1.5}
        strokeDasharray="3 3" dot={false} activeDot={false}
      />
      {/* Total value — main line */}
      <Line
        type="monotone" dataKey="total"
        stroke="#00d4ff" strokeWidth={2.5}
        dot={false} activeDot={{ r:4, fill:'#00d4ff' }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Crossover + breakdown tiles */}
  {(() => {
    const crossover = chartData.find(d => d.gains >= d.contributions);
    const last      = chartData[chartData.length - 1];
    return (
      <>
        <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
          {[
            { label:'You invested',   value:fmt(invested, sym),  color:'#4d8fff' },
            { label:'Market added',   value:fmt(gains, sym),     color:'#2ecc71' },
            { label:'Return multiple',value:`${(totalFV/invested).toFixed(1)}x`, color:'#f1c40f' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              padding:'10px 12px', borderRadius:8,
              background:`${color}18`, border:`1px solid ${color}35`,
              fontSize:11.5, textAlign:'center',
            }}>
              <div style={{ color:'rgba(220,230,250,.45)', marginBottom:3 }}>{label}</div>
              <div style={{ color, fontWeight:700, fontSize:13, fontFamily:'monospace' }}>{value}</div>
            </div>
          ))}
        </div>

        {crossover && (
          <div style={{
            marginTop:10, padding:'10px 14px', borderRadius:8,
            background:'rgba(0,212,255,.08)', border:'1px solid rgba(0,212,255,.2)',
            fontSize:12.5, color:'rgba(220,230,250,.55)', lineHeight:1.7,
          }}>
            📈 Market gains overtake your contributions in{' '}
            <span style={{ color:'#00d4ff', fontWeight:700 }}>
              {crossover.year - new Date().getFullYear()} years
            </span>
            {' '}({crossover.year}) — after that compounding does more work than your monthly SIP.
          </div>
        )}
      </>
    );
  })()}
</div>
    </div>
  );
}

/* ── Retirement Calculator ───────────────────────────────────────── */
export function RetirementCalc({ sym='$' }) {
  const [currentAge,   setCurrentAge]   = useState(30);
  const [retireAge,    setRetireAge]    = useState(65);
  const [lifeExp,      setLifeExp]      = useState(90);
  const [currentSave,  setCurrentSave]  = useState(50000);
  const [annualContrib,setAnnualContrib]= useState(12000);
  const [preRetReturn, setPreRetReturn] = useState(7);
  const [postRetReturn,setPostRetReturn]= useState(4);
  const [annualExpRet, setAnnualExpRet] = useState(60000);

  const yearsToRetire  = retireAge - currentAge;
  const retireYears    = lifeExp - retireAge;
  const r1             = preRetReturn/100;
  const nestEgg        = currentSave*Math.pow(1+r1,yearsToRetire) + annualContrib*(Math.pow(1+r1,yearsToRetire)-1)/r1;
  const r2             = postRetReturn/100;
  const canWithdraw    = r2>0 ? nestEgg * r2 / (1-Math.pow(1+r2,-retireYears)) : nestEgg/retireYears;
  const shortfall      = annualExpRet - canWithdraw;
  const isOkay         = canWithdraw >= annualExpRet;
const chartData = useMemo(() => {
  const points = [];
  const currentYear = new Date().getFullYear();

  // Phase 1: Accumulation (now → retirement)
  for (let y = 0; y <= yearsToRetire; y++) {
    const balance = currentSave * Math.pow(1 + r1, y)
      + annualContrib * (Math.pow(1 + r1, y) - 1) / r1;
    points.push({
      year:    currentYear + y,
      age:     currentAge + y,
      balance: Math.round(Math.max(0, balance)),
      phase:   'accumulation',
      target:  Math.round(nestEgg),
    });
  }

  // Phase 2: Drawdown (retirement → life expectancy)
  let balance = nestEgg;
  for (let y = 1; y <= retireYears; y++) {
    balance = balance * (1 + r2) - annualExpRet;
    points.push({
      year:    currentYear + yearsToRetire + y,
      age:     retireAge + y,
      balance: Math.round(Math.max(0, balance)),
      phase:   'drawdown',
      target:  null,
    });
    if (balance <= 0) break;
  }

  return points;
}, [currentAge, retireAge, currentSave, annualContrib, r1, r2, nestEgg, annualExpRet, yearsToRetire, retireYears]);

// Find when money runs out
const runsOutAge = (() => {
  const drawdown = chartData.filter(d => d.phase === 'drawdown');
  const empty    = drawdown.find(d => d.balance === 0);
  return empty ? empty.age : null;
})();
const lastsFullRetirement = runsOutAge === null || runsOutAge >= lifeExp;
  return (
    <div>
      <div  style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label={`Current Age: ${currentAge}`}><input type="range" min={18} max={70} value={currentAge} onChange={e=>setCurrentAge(+e.target.value)} style={SLIDER('#9b59b6')}/></Field>
        <Field label={`Retirement Age: ${retireAge}`}><input type="range" min={50} max={80} value={retireAge} onChange={e=>setRetireAge(+e.target.value)} style={SLIDER('#9b59b6')}/></Field>
        <Field label={`Life Expectancy: ${lifeExp}`}><input type="range" min={70} max={100} value={lifeExp} onChange={e=>setLifeExp(+e.target.value)} style={SLIDER('#9b59b6')}/></Field>
        <Field label="Current Retirement Savings"><input type="number" value={currentSave} min={0} onChange={e=>setCurrentSave(+e.target.value)} style={INPUT}/></Field>
        <Field label="Annual Contribution"><input type="number" value={annualContrib} min={0} onChange={e=>setAnnualContrib(+e.target.value)} style={INPUT}/></Field>
        <Field label="Annual Expenses in Retirement"><input type="number" value={annualExpRet} min={0} onChange={e=>setAnnualExpRet(+e.target.value)} style={INPUT}/></Field>
        <Field label={`Pre-Retirement Return: ${preRetReturn}%`}><input type="range" min={1} max={15} step={0.5} value={preRetReturn} onChange={e=>setPreRetReturn(+e.target.value)} style={SLIDER('#9b59b6')}/></Field>
        <Field label={`Post-Retirement Return: ${postRetReturn}%`}><input type="range" min={1} max={10} step={0.5} value={postRetReturn} onChange={e=>setPostRetReturn(+e.target.value)} style={SLIDER('#9b59b6')}/></Field>
      </div>
      <ResultGrid>
        <ResultCard label="Nest Egg at Retirement" value={fmt(nestEgg,sym)} color="#9b59b6" large/>
        <ResultCard label="Annual Withdrawal" value={fmt(canWithdraw,sym)} color={isOkay?'#2ecc71':'#e74c3c'}/>
        <ResultCard label={isOkay?'Annual Surplus':'Annual Shortfall'} value={fmt(Math.abs(shortfall),sym)} color={isOkay?'#2ecc71':'#e74c3c'}/>
        <ResultCard label="Years to Retire" value={`${yearsToRetire}`} color="#00d4ff"/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:'rgba(220,230,250,.45)',
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    Retirement Portfolio — Accumulation & Drawdown
  </div>

  {!lastsFullRetirement && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:'rgba(231,76,60,.12)', border:'1px solid rgba(231,76,60,.3)',
      fontSize:12.5, color:'#e74c3c',
    }}>
      ⚠ Portfolio runs out at age <strong>{runsOutAge}</strong> —{' '}
      {lifeExp - runsOutAge} years short of your life expectancy.
      Increase contributions, delay retirement, or reduce expenses.
    </div>
  )}

  {lastsFullRetirement && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:'rgba(46,204,113,.1)', border:'1px solid rgba(46,204,113,.3)',
      fontSize:12.5, color:'#2ecc71',
    }}>
      ✅ Portfolio lasts through age <strong>{lifeExp}</strong> — you're on track.
      {chartData[chartData.length-1]?.balance > 0 &&
        ` Leaving ${sym}${chartData[chartData.length-1].balance.toLocaleString()} as legacy.`}
    </div>
  )}

  <ResponsiveContainer width="100%" height={280}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="age"
        tick={{ fill:'rgba(220,230,250,.45)', fontSize:11 }}
        tickLine={false} axisLine={false}
        interval="preserveStartEnd"
        tickFormatter={v => `Age ${v}`}
      />
      <YAxis
        tick={{ fill:'rgba(220,230,250,.45)', fontSize:11 }}
        tickLine={false} axisLine={false}
        width={64}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{ background:'#0c0f18', border:'1px solid rgba(255,255,255,.07)', borderRadius:8, fontSize:12 }}
        labelStyle={{ color:'rgba(220,230,250,.45)', marginBottom:4 }}
        itemStyle={{ color:'#dce6fa' }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'balance' ? 'Portfolio balance' : 'Nest egg target',
        ]}
        labelFormatter={age => `Age ${age}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:'rgba(220,230,250,.45)', paddingTop:8 }}
        formatter={name => name === 'balance' ? 'Portfolio balance' : 'Nest egg at retirement'}
      />

      {/* Retirement start marker */}
      <ReferenceLine
        x={retireAge}
        stroke="#9b59b6"
        strokeDasharray="4 3"
        strokeWidth={1.5}
        label={{ value:'Retire', position:'top', fill:'#9b59b6', fontSize:10, fontWeight:700 }}
      />

      {/* Life expectancy marker */}
      <ReferenceLine
        x={lifeExp}
        stroke="rgba(220,230,250,.2)"
        strokeDasharray="3 3"
        strokeWidth={1}
        label={{ value:'Life exp.', position:'top', fill:'rgba(220,230,250,.3)', fontSize:10 }}
      />

      {/* Runs out marker */}
      {runsOutAge && (
        <ReferenceLine
          x={runsOutAge}
          stroke="#e74c3c"
          strokeDasharray="4 3"
          strokeWidth={1.5}
          label={{ value:'Runs out', position:'top', fill:'#e74c3c', fontSize:10, fontWeight:700 }}
        />
      )}

      {/* Nest egg target line (flat at retirement value, only during accumulation) */}
      <Line
        type="monotone" dataKey="target"
        stroke="#9b59b6" strokeWidth={1}
        strokeDasharray="6 4" dot={false} activeDot={false}
        connectNulls={false}
      />

      {/* Portfolio balance — color shifts at retirement */}
      <Line
        type="monotone" dataKey="balance"
        stroke="#00d4ff" strokeWidth={2.5}
        dot={false} activeDot={{ r:4, fill:'#00d4ff' }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Phase summary */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
    {[
      { label:'Years accumulating', value:`${yearsToRetire} yrs`,   color:'#00d4ff' },
      { label:'Nest egg',           value:fmt(nestEgg, sym),         color:'#9b59b6' },
      { label:'Years in retirement',value:`${retireYears} yrs`,      color:'#2ecc71' },
      { label:lastsFullRetirement ? 'Portfolio lasts' : 'Runs out at',
        value:lastsFullRetirement
          ? `Through ${lifeExp}`
          : `Age ${runsOutAge}`,
        color:lastsFullRetirement ? '#2ecc71' : '#e74c3c',
      },
    ].map(({ label, value, color }) => (
      <div key={label} style={{
        padding:'9px 10px', borderRadius:8,
        background:`${color}18`, border:`1px solid ${color}35`,
        fontSize:11.5, textAlign:'center',
      }}>
        <div style={{ color:'rgba(220,230,250,.45)', marginBottom:3 }}>{label}</div>
        <div style={{ color, fontWeight:700, fontSize:12.5 }}>{value}</div>
      </div>
    ))}
  </div>
</div>
    </div>
  );
}

/* ── RRSP Contribution Calculator ────────────────────────────────── */
export function RRSPCalc({ sym='CA$' }) {
  const [income,    setIncome]    = useState(95000);
  const [contribPct,setContribPct]= useState(18);
  const [existing,  setExisting]  = useState(15000);
  const [unused,    setUnused]    = useState(10000);
  const [marginal,  setMarginal]  = useState(43.41);

  const annualLimit    = 31560; // 2024 RRSP limit
  const contribLimit   = Math.min(income * contribPct/100, annualLimit);
  const totalRoom      = contribLimit + unused;
  const taxSaving      = (contribPct/100 * Math.min(income,totalRoom)) * marginal/100;
  const effective      = (taxSaving / totalRoom) * 100;
const [returnRate, setReturnRate] = useState(6);
const [years,      setYears]      = useState(25);

const r           = returnRate / 100;
const totalContrib = totalRoom; // this year's contribution

const chartData = useMemo(() => {
  return Array.from({ length: years + 1 }, (_, y) => {
    // Base: existing balance + this year's contribution, compounding
    const base = (existing + totalContrib) * Math.pow(1 + r, y);
    // With refund reinvested: add tax saving as extra contribution
    const withRefund = (existing + totalContrib + taxSaving) * Math.pow(1 + r, y);
    // Without RRSP: invested in taxable account at lower after-tax return
    const taxDrag    = r * (1 - marginal / 100);
    const taxable    = (existing + totalContrib) * Math.pow(1 + taxDrag, y);

    return {
      year:        new Date().getFullYear() + y,
      rrsp:        Math.round(Math.max(0, base)),
      withRefund:  Math.round(Math.max(0, withRefund)),
      taxable:     Math.round(Math.max(0, taxable)),
    };
  });
}, [existing, totalContrib, taxSaving, r, marginal, years]);
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Annual Employment Income"><input type="number" value={income} min={0} onChange={e=>setIncome(+e.target.value)} style={INPUT}/></Field>
        <Field label="Existing RRSP Balance"><input type="number" value={existing} min={0} onChange={e=>setExisting(+e.target.value)} style={INPUT}/></Field>
        <Field label="Unused Contribution Room (from NOA)"><input type="number" value={unused} min={0} onChange={e=>setUnused(+e.target.value)} style={INPUT}/></Field>
        <Field label={`Marginal Tax Rate: ${marginal}%`} help="Ontario 43.41% on $90k+"><input type="range" min={20} max={55} step={0.5} value={marginal} onChange={e=>setMarginal(+e.target.value)} style={SLIDER('#e74c3c')}/></Field>
          <Field label={`Return Rate: ${returnRate}%`}>
      <input type="range" min={1} max={12} step={0.5} value={returnRate}
        onChange={e=>setReturnRate(+e.target.value)} style={SLIDER('#e74c3c')}/>
    </Field>
    <Field label={`Years: ${years}`}>
      <input type="range" min={1} max={40} value={years}
        onChange={e=>setYears(+e.target.value)} style={SLIDER('#e74c3c')}/>
    </Field>
      </div>
      <div style={{ padding:'10px 12px', background:'rgba(231,76,60,.08)', border:'1px solid rgba(231,76,60,.2)', borderRadius:8, fontSize:12, color:'rgba(231,76,60,.9)', marginBottom:4 }}>
        🍁 2024 RRSP limit: CA$31,560 | 18% of prior year earned income
      </div>
      <ResultGrid>
        <ResultCard label="This Year's RRSP Room" value={fmt(contribLimit,sym)} color="#e74c3c" large/>
        <ResultCard label="Total Room (with unused)" value={fmt(totalRoom,sym)} color="#4d8fff"/>
        <ResultCard label="Estimated Tax Saving" value={fmt(taxSaving,sym)} color="#2ecc71"/>
        <ResultCard label="Effective Return" value={fmtPct(effective)} color="#f1c40f"/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:'rgba(220,230,250,.45)',
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    RRSP Growth Projection — {years} Years
  </div>

  <ResponsiveContainer width="100%" height={260}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="year"
        tick={{ fill:'rgba(220,230,250,.45)', fontSize:11 }}
        tickLine={false} axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:'rgba(220,230,250,.45)', fontSize:11 }}
        tickLine={false} axisLine={false}
        width={64}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{ background:'#0c0f18', border:'1px solid rgba(255,255,255,.07)', borderRadius:8, fontSize:12 }}
        labelStyle={{ color:'rgba(220,230,250,.45)', marginBottom:4 }}
        itemStyle={{ color:'#dce6fa' }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'rrsp'       ? 'RRSP balance'                  :
          name === 'withRefund' ? 'RRSP + refund reinvested'      :
          'Taxable account (after tax drag)',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:'rgba(220,230,250,.45)', paddingTop:8 }}
        formatter={name =>
          name === 'rrsp'       ? 'RRSP (no refund reinvested)'   :
          name === 'withRefund' ? 'RRSP + refund reinvested'       :
          'Taxable account'
        }
      />

      {/* Taxable account — worst case */}
      <Line
        type="monotone" dataKey="taxable"
        stroke="#e74c3c" strokeWidth={1.5}
        strokeDasharray="4 3" dot={false} activeDot={false}
      />
      {/* RRSP base */}
      <Line
        type="monotone" dataKey="rrsp"
        stroke="#4d8fff" strokeWidth={2}
        dot={false} activeDot={{ r:4, fill:'#4d8fff' }}
      />
      {/* RRSP with refund reinvested — best case */}
      <Line
        type="monotone" dataKey="withRefund"
        stroke="#2ecc71" strokeWidth={2.5}
        dot={false} activeDot={{ r:4, fill:'#2ecc71' }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Year N comparison tiles */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
    {(() => {
      const last = chartData[chartData.length - 1];
      const extraFromRefund = last.withRefund - last.rrsp;
      const rrspVsTaxable   = last.rrsp      - last.taxable;
      return [
        { label:`RRSP in ${years} yrs`,          value:fmt(last.rrsp, sym),       color:'#4d8fff' },
        { label:'Extra from refund reinvested',   value:fmt(extraFromRefund, sym), color:'#2ecc71' },
        { label:'RRSP advantage over taxable',    value:fmt(rrspVsTaxable, sym),   color:'#f1c40f' },
      ];
    })().map(({ label, value, color }) => (
      <div key={label} style={{
        padding:'10px 12px', borderRadius:8,
        background:`${color}18`, border:`1px solid ${color}35`,
        fontSize:11.5, textAlign:'center',
      }}>
        <div style={{ color:'rgba(220,230,250,.45)', marginBottom:3 }}>{label}</div>
        <div style={{ color, fontWeight:700, fontSize:13, fontFamily:'monospace' }}>{value}</div>
      </div>
    ))}
  </div>

  {/* RRSP triple benefit callout */}
  <div style={{
    marginTop:10, display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:7,
  }}>
    {[
      { icon:'1️⃣', label:'Contribution deduction', desc:`${sym}${taxSaving.toFixed(0)} tax refund this year at ${marginal}% rate`, color:'#e74c3c' },
      { icon:'2️⃣', label:'Tax-sheltered growth',    desc:`Full ${returnRate}% return — no annual tax drag`,                        color:'#4d8fff' },
      { icon:'3️⃣', label:'Withdraw in lower bracket',desc:'Taxed only on withdrawal, usually at lower retirement income rate',     color:'#2ecc71' },
    ].map(({ icon, label, desc, color }) => (
      <div key={label} style={{
        padding:'10px 11px', borderRadius:8,
        background:`${color}10`, border:`1px solid ${color}25`,
        fontSize:11,
      }}>
        <div style={{ fontSize:15, marginBottom:4 }}>{icon}</div>
        <div style={{ color, fontWeight:700, marginBottom:3 }}>{label}</div>
        <div style={{ color:'rgba(220,230,250,.4)', lineHeight:1.5 }}>{desc}</div>
      </div>
    ))}
  </div>
</div>
    </div>
  );
}

/* ── TFSA Growth Calculator ──────────────────────────────────────── */
export function TFSACalc({ sym='CA$' }) {
  const [annual,   setAnnual]   = useState(7000);
  const [current,  setCurrent]  = useState(20000);
  const [rate,     setRate]     = useState(7);
  const [years,    setYears]    = useState(25);
  const [unused,   setUnused]   = useState(0);

  const TFSA_ANNUAL_LIMIT = 7000; // 2024
  const r       = rate/100;
  const contrib = Math.min(annual, TFSA_ANNUAL_LIMIT);
  const finalBal= (current + unused) * Math.pow(1+r,years) + contrib * (Math.pow(1+r,years)-1)/r;
  const invested = current + unused + contrib*years;
  const growth   = finalBal - invested;
const [marginal, setMarginal] = useState(43.41);

const chartData = useMemo(() => {
  return Array.from({ length: years + 1 }, (_, y) => {
    const balance      = (current + unused) * Math.pow(1 + r, y)
      + contrib * (Math.pow(1 + r, y) - 1) / r;
    const contributions = current + unused + contrib * y;
    const gains_        = Math.max(0, balance - contributions);

    // Taxable account: same contributions but tax drag on returns
    const taxDrag    = r * (1 - marginal / 100);
    const taxable    = (current + unused) * Math.pow(1 + taxDrag, y)
      + contrib * (Math.pow(1 + taxDrag, y) - 1) / taxDrag;

    return {
      year:         new Date().getFullYear() + y,
      balance:      Math.round(Math.max(0, balance)),
      contributions:Math.round(contributions),
      gains:        Math.round(gains_),
      taxable:      Math.round(Math.max(0, taxable)),
    };
  });
}, [current, unused, contrib, r, marginal, years]);
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Annual Contribution" help="2024 max: CA$7,000"><input type="number" value={annual} min={0} max={7000} onChange={e=>setAnnual(+e.target.value)} style={INPUT}/></Field>
        <Field label="Current TFSA Balance"><input type="number" value={current} min={0} onChange={e=>setCurrent(+e.target.value)} style={INPUT}/></Field>
        <Field label="Unused TFSA Room (lifetime)"><input type="number" value={unused} min={0} onChange={e=>setUnused(+e.target.value)} style={INPUT}/></Field>
        <Field label={`Investment Return: ${rate}%`}><input type="range" min={1} max={15} step={0.5} value={rate} onChange={e=>setRate(+e.target.value)} style={SLIDER('#1abc9c')}/></Field>
        <Field label={`Years: ${years}`}><input type="range" min={1} max={40} value={years} onChange={e=>setYears(+e.target.value)} style={SLIDER('#1abc9c')}/></Field><Field label={`Your Marginal Tax Rate: ${marginal}%`} help="Used for taxable account comparison">
  <input type="range" min={20} max={55} step={0.5} value={marginal}
    onChange={e=>setMarginal(+e.target.value)} style={SLIDER('#1abc9c')}/>
</Field>
      
      </div>
      <div style={{ padding:'10px 12px', background:'rgba(26,188,156,.08)', border:'1px solid rgba(26,188,156,.2)', borderRadius:8, fontSize:12, color:'rgba(26,188,156,.9)', marginBottom:4 }}>
        💎 All growth and withdrawals are tax-free in a TFSA
      </div>
      <ResultGrid>
        <ResultCard label="Tax-Free Future Value" value={fmt(finalBal,sym)} color="#1abc9c" large/>
        <ResultCard label="Total Contributed" value={fmt(invested,sym)} color="#4d8fff"/>
        <ResultCard label="Tax-Free Growth" value={fmt(growth,sym)} color="#2ecc71"/>
        <ResultCard label="Annual Limit" value={fmt(contrib,sym)} color="#f1c40f"/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:'rgba(220,230,250,.45)',
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    TFSA Growth — Tax-Free vs Taxable Account
  </div>

  <ResponsiveContainer width="100%" height={260}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="year"
        tick={{ fill:'rgba(220,230,250,.45)', fontSize:11 }}
        tickLine={false} axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:'rgba(220,230,250,.45)', fontSize:11 }}
        tickLine={false} axisLine={false}
        width={64}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{ background:'#0c0f18', border:'1px solid rgba(255,255,255,.07)', borderRadius:8, fontSize:12 }}
        labelStyle={{ color:'rgba(220,230,250,.45)', marginBottom:4 }}
        itemStyle={{ color:'#dce6fa' }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'balance'       ? 'TFSA balance (tax-free)'    :
          name === 'contributions' ? 'Total contributions'         :
          name === 'gains'         ? 'Tax-free gains'              :
          'Taxable account (after tax drag)',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:'rgba(220,230,250,.45)', paddingTop:8 }}
        formatter={name =>
          name === 'balance'       ? 'TFSA balance'       :
          name === 'contributions' ? 'Contributions'        :
          name === 'gains'         ? 'Tax-free gains'       :
          'Taxable account'
        }
      />

      {/* Taxable account — shows what you lose to tax drag */}
      <Line
        type="monotone" dataKey="taxable"
        stroke="#e74c3c" strokeWidth={1.5}
        strokeDasharray="4 3" dot={false} activeDot={false}
      />
      {/* Contributions */}
      <Line
        type="monotone" dataKey="contributions"
        stroke="#4d8fff" strokeWidth={1.5}
        strokeDasharray="6 4" dot={false} activeDot={false}
      />
      {/* Tax-free gains */}
      <Line
        type="monotone" dataKey="gains"
        stroke="#2ecc71" strokeWidth={1.5}
        strokeDasharray="3 3" dot={false} activeDot={false}
      />
      {/* TFSA total balance — main line */}
      <Line
        type="monotone" dataKey="balance"
        stroke="#1abc9c" strokeWidth={2.5}
        dot={false} activeDot={{ r:4, fill:'#1abc9c' }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Year N comparison tiles */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
    {(() => {
      const last         = chartData[chartData.length - 1];
      const tfsaAdvantage = last.balance - last.taxable;
      return [
        { label:`TFSA in ${years} yrs`,      value:fmt(last.balance, sym),      color:'#1abc9c' },
        { label:'Tax-free gains',             value:fmt(last.gains, sym),        color:'#2ecc71' },
        { label:'Taxable account value',      value:fmt(last.taxable, sym),      color:'#e74c3c' },
        { label:'TFSA advantage',             value:fmt(tfsaAdvantage, sym),     color:'#f1c40f' },
      ];
    })().map(({ label, value, color }) => (
      <div key={label} style={{
        padding:'10px 12px', borderRadius:8,
        background:`${color}18`, border:`1px solid ${color}35`,
        fontSize:11.5, textAlign:'center',
      }}>
        <div style={{ color:'rgba(220,230,250,.45)', marginBottom:3 }}>{label}</div>
        <div style={{ color, fontWeight:700, fontSize:13, fontFamily:'monospace' }}>{value}</div>
      </div>
    ))}
  </div>

  {/* TFSA key advantages */}
  <div style={{ marginTop:10, display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:7 }}>
    {[
      {
        icon:'💎',
        label:'Tax-free growth',
        desc:`${sym}${Math.round(growth).toLocaleString()} earned — zero capital gains or income tax`,
        color:'#1abc9c',
      },
      {
        icon:'🔄',
        label:'Withdrawals restore room',
        desc:'Money withdrawn in any year adds back contribution room the following year',
        color:'#2ecc71',
      },
      {
        icon:'🍁',
        label:'No income effect',
        desc:'TFSA withdrawals do not affect OAS, GIS, or income-tested benefits',
        color:'#4d8fff',
      },
    ].map(({ icon, label, desc, color }) => (
      <div key={label} style={{
        padding:'10px 11px', borderRadius:8,
        background:`${color}10`, border:`1px solid ${color}25`,
        fontSize:11,
      }}>
        <div style={{ fontSize:15, marginBottom:4 }}>{icon}</div>
        <div style={{ color, fontWeight:700, marginBottom:3 }}>{label}</div>
        <div style={{ color:'rgba(220,230,250,.4)', lineHeight:1.5 }}>{desc}</div>
      </div>
    ))}
  </div>

  {/* Crossover insight */}
  {(() => {
    const crossover = chartData.find(d => d.gains >= d.contributions);
    return crossover ? (
      <div style={{
        marginTop:10, padding:'10px 14px', borderRadius:8,
        background:'rgba(26,188,156,.08)', border:'1px solid rgba(26,188,156,.2)',
        fontSize:12.5, color:'rgba(220,230,250,.55)', lineHeight:1.7,
      }}>
        📈 Tax-free gains overtake contributions in{' '}
        <span style={{ color:'#1abc9c', fontWeight:700 }}>
          {crossover.year - new Date().getFullYear()} years
        </span>
        {' '}({crossover.year}). At {marginal}% marginal rate, the TFSA advantage over a taxable
        account reaches{' '}
        <span style={{ color:'#f1c40f', fontWeight:700 }}>
          {sym}{(chartData[chartData.length-1].balance - chartData[chartData.length-1].taxable).toLocaleString()}
        </span>
        {' '}by year {years}.
      </div>
    ) : null;
  })()}
</div>
    </div>
  );
}

/* ── Dividend Yield Calculator ───────────────────────────────────── */
export function DividendCalc({ sym='$' }) {
  const [shares,      setShares]      = useState(100);
  const [price,       setPrice]       = useState(50);
  const [annualDiv,   setAnnualDiv]   = useState(2.40);
  const [growthRate,  setGrowthRate]  = useState(5);
  const [years,       setYears]       = useState(10);

  const investment    = shares * price;
  const divYield      = price > 0 ? (annualDiv/price)*100 : 0;
  const annualIncome  = shares * annualDiv;
  const futureIncome  = annualIncome * Math.pow(1+growthRate/100, years);
  const totalDivs     = shares * annualDiv * ((Math.pow(1+growthRate/100,years)-1)/(growthRate/100));
const chartData = useMemo(() => {
  return Array.from({ length: years + 1 }, (_, y) => {
    const annualIncome_ = shares * annualDiv * Math.pow(1 + growthRate / 100, y);
    const cumulative    = y === 0 ? 0
      : shares * annualDiv * ((Math.pow(1 + growthRate / 100, y) - 1) / (growthRate / 100));
    return {
      year:       new Date().getFullYear() + y,
      annual:     Math.round(annualIncome_ * 100) / 100,
      cumulative: Math.round(cumulative),
      investment: investment,   // flat line — breakeven when cumulative crosses this
    };
  });
}, [shares, annualDiv, growthRate, years, investment]);

const breakEvenYear = chartData.find(d => d.cumulative >= investment)?.year ?? null;
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Number of Shares"><input type="number" value={shares} min={1} onChange={e=>setShares(+e.target.value)} style={INPUT}/></Field>
        <Field label="Current Share Price"><input type="number" value={price} min={0} step={0.01} onChange={e=>setPrice(+e.target.value)} style={INPUT}/></Field>
        <Field label="Annual Dividend per Share"><input type="number" value={annualDiv} min={0} step={0.01} onChange={e=>setAnnualDiv(+e.target.value)} style={INPUT}/></Field>
        <Field label={`Dividend Growth Rate: ${growthRate}%`}><input type="range" min={0} max={20} step={0.5} value={growthRate} onChange={e=>setGrowthRate(+e.target.value)} style={SLIDER('#f1c40f')}/></Field>
        <Field label={`Years: ${years}`}><input type="range" min={1} max={30} value={years} onChange={e=>setYears(+e.target.value)} style={SLIDER('#f1c40f')}/></Field>
      </div>
      <ResultGrid>
        <ResultCard label="Dividend Yield" value={fmtPct(divYield,2)} color="#f1c40f" large/>
        <ResultCard label="Annual Income (now)" value={fmt(annualIncome,sym,2)} color="#2ecc71"/>
        <ResultCard label={`Annual Income (yr ${years})`} value={fmt(futureIncome,sym,2)} color="#00d4ff"/>
        <ResultCard label={`Total Dividends (${years}yr)`} value={fmt(totalDivs,sym)} color="#4d8fff"/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>

  {/* ── Annual dividend income ─────────────────────────────── */}
  <div style={{
    fontSize:12, fontWeight:700, color:'rgba(220,230,250,.45)',
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    Annual Dividend Income Over Time
  </div>

  <ResponsiveContainer width="100%" height={220}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="year"
        tick={{ fill:'rgba(220,230,250,.45)', fontSize:11 }}
        tickLine={false} axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:'rgba(220,230,250,.45)', fontSize:11 }}
        tickLine={false} axisLine={false}
        width={64}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{ background:'#0c0f18', border:'1px solid rgba(255,255,255,.07)', borderRadius:8, fontSize:12 }}
        labelStyle={{ color:'rgba(220,230,250,.45)', marginBottom:4 }}
        itemStyle={{ color:'#dce6fa' }}
        formatter={(value, name) => [
          `${sym}${Number(value).toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 })}`,
          'Annual dividend income',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Line
        type="monotone" dataKey="annual"
        stroke="#f1c40f" strokeWidth={2.5}
        dot={false} activeDot={{ r:4, fill:'#f1c40f' }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* ── Cumulative dividends vs investment ────────────────── */}
  <div style={{
    fontSize:12, fontWeight:700, color:'rgba(220,230,250,.45)',
    textTransform:'uppercase', letterSpacing:'.08em',
    marginBottom:8, marginTop:24,
  }}>
    Cumulative Dividends — Breakeven on Investment
  </div>
  <div style={{ fontSize:12, color:'rgba(220,230,250,.4)', marginBottom:10 }}>
    When cumulative dividends cross your initial investment, you've been paid back in full — everything after is pure profit.
  </div>

  <ResponsiveContainer width="100%" height={200}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="year"
        tick={{ fill:'rgba(220,230,250,.45)', fontSize:11 }}
        tickLine={false} axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:'rgba(220,230,250,.45)', fontSize:11 }}
        tickLine={false} axisLine={false}
        width={64}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{ background:'#0c0f18', border:'1px solid rgba(255,255,255,.07)', borderRadius:8, fontSize:12 }}
        labelStyle={{ color:'rgba(220,230,250,.45)', marginBottom:4 }}
        itemStyle={{ color:'#dce6fa' }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'cumulative' ? 'Cumulative dividends' : 'Initial investment',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:'rgba(220,230,250,.45)', paddingTop:8 }}
        formatter={name =>
          name === 'cumulative' ? 'Cumulative dividends' : 'Initial investment (breakeven)'
        }
      />

      {/* Breakeven marker */}
      {breakEvenYear && (
        <ReferenceLine
          x={breakEvenYear}
          stroke="#2ecc71"
          strokeDasharray="4 3"
          strokeWidth={1.5}
          label={{
            value:`Paid back ${breakEvenYear}`,
            position:'top',
            fill:'#2ecc71',
            fontSize:10,
            fontWeight:700,
          }}
        />
      )}

      {/* Investment line — flat breakeven target */}
      <Line
        type="monotone" dataKey="investment"
        stroke="#4d8fff" strokeWidth={1.5}
        strokeDasharray="6 4" dot={false} activeDot={false}
      />
      {/* Cumulative dividends */}
      <Line
        type="monotone" dataKey="cumulative"
        stroke="#2ecc71" strokeWidth={2.5}
        dot={false} activeDot={{ r:4, fill:'#2ecc71' }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Summary tiles */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
    {[
      { label:'Initial investment',          value:fmt(investment, sym),                                        color:'#4d8fff' },
      { label:`Income in yr ${years}`,       value:fmt(futureIncome, sym, 2),                                   color:'#f1c40f' },
      { label:`Total dividends (${years}yr)`,value:fmt(totalDivs, sym),                                         color:'#2ecc71' },
      { label:'Paid back by dividends',      value:breakEvenYear ? `${breakEvenYear}` : `>${new Date().getFullYear() + years}`, color:breakEvenYear ? '#2ecc71' : 'rgba(220,230,250,.3)' },
    ].map(({ label, value, color }) => (
      <div key={label} style={{
        padding:'9px 10px', borderRadius:8,
        background:`${color}18`, border:`1px solid ${color}35`,
        fontSize:11.5, textAlign:'center',
      }}>
        <div style={{ color:'rgba(220,230,250,.45)', marginBottom:3 }}>{label}</div>
        <div style={{ color, fontWeight:700, fontSize:12.5, fontFamily:'monospace' }}>{value}</div>
      </div>
    ))}
  </div>

  {/* Insight callout */}
  <div style={{
    marginTop:10, padding:'10px 14px', borderRadius:8,
    background:'rgba(241,196,15,.08)', border:'1px solid rgba(241,196,15,.2)',
    fontSize:12.5, color:'rgba(220,230,250,.55)', lineHeight:1.7,
  }}>
    Your{' '}
    <span style={{ color:'#f1c40f', fontWeight:700 }}>{divYield.toFixed(2)}% yield</span>
    {' '}generates{' '}
    <span style={{ color:'#2ecc71', fontWeight:700 }}>
      {sym}{annualIncome.toFixed(2)}/yr
    </span>
    {' '}today, growing to{' '}
    <span style={{ color:'#00d4ff', fontWeight:700 }}>
      {sym}{futureIncome.toFixed(2)}/yr
    </span>
    {' '}in {years} years at {growthRate}% annual dividend growth.
    {breakEvenYear
      ? ` Dividends alone pay back your full investment by ${breakEvenYear}.`
      : ` Extend the timeline to see when dividends pay back your investment.`}
  </div>
</div>
    </div>
  );
}

/* ── Currency Converter ──────────────────────────────────────────── */
export function CurrencyConverter() {
  const [amount,   setAmount]   = useState(1000);
  const [from,     setFrom]     = useState('USD');
  const [to,       setTo]       = useState('CAD');
  const [rates,    setRates]    = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [lastUpdate,setLastUpdate]= useState('');
  const [error,    setError]    = useState('');

  // Approximate rates (fallback — in production use an API)
  const FALLBACK_RATES = {
    USD:1, CAD:1.36, GBP:0.79, EUR:0.92, AUD:1.53, INR:83.1,
    JPY:149.2, SGD:1.34, CHF:0.88, NZD:1.63, MXN:17.1, BRL:4.97,
  };

  const fetchRates = async () => {
    setLoading(true); setError('');
    try {
      const res  = await fetch(`https://api.exchangerate-api.com/v4/latest/${from}`);
      const data = await res.json();
      setRates(data.rates);
      setLastUpdate(new Date().toLocaleString());
    } catch {
      setRates(FALLBACK_RATES);
      setLastUpdate('Using approximate rates');
    }
    setLoading(false);
  };

  useEffect(() => { fetchRates(); }, [from]);

  const rate      = rates ? (rates[to] / (rates[from] || 1)) : FALLBACK_RATES[to] / FALLBACK_RATES[from];
  const converted = amount * rate;

  const CURRENCIES = Object.keys(FALLBACK_RATES);
  const swap       = () => { setFrom(to); setTo(from); };

  return (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:14, alignItems:'flex-end', flexWrap:'wrap' }}>
        <div style={{ flex:1 }}>
          <Field label="Amount">
            <input type="number" value={amount} min={0} onChange={e=>setAmount(+e.target.value)} style={INPUT}/>
          </Field>
        </div>
        <div style={{ flex:1 }}>
          <Field label="From">
            <select value={from} onChange={e=>setFrom(e.target.value)} style={{ ...INPUT, fontFamily:'sans-serif' }}>
              {CURRENCIES.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <button onClick={swap} style={{ padding:'8px 12px', borderRadius:7, border:'1px solid rgba(255,255,255,.09)', background:'rgba(255,255,255,.05)', color:'rgba(255,255,255,0.75)', cursor:'pointer', marginBottom:14, fontSize:16 }}>⇄</button>
        <div style={{ flex:1 }}>
          <Field label="To">
            <select value={to} onChange={e=>setTo(e.target.value)} style={{ ...INPUT, fontFamily:'sans-serif' }}>
              {CURRENCIES.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <div style={{ padding:'20px', textAlign:'center', background:'rgba(0,212,255,.06)', border:'1px solid rgba(0,212,255,.2)', borderRadius:12, marginBottom:12 }}>
        <div style={{ fontSize:14, color:'rgba(255,255,255,0.75)', marginBottom:8 }}>
          {amount.toLocaleString()} {from} =
        </div>
        <div style={{ fontSize:32, fontWeight:800, color:'#00d4ff', fontFamily:'monospace' }}>
          {loading ? '…' : converted.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} {to}
        </div>
        <div style={{ fontSize:12, color:'rgba(255,255,255,0.75)', marginTop:8 }}>
          1 {from} = {rate.toFixed(4)} {to}
          {lastUpdate && ` · ${lastUpdate}`}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:8 }}>
        {[50,100,500,1000,5000,10000].map(n=>(
          <button key={n} onClick={()=>setAmount(n)} style={{ padding:'7px', borderRadius:7, border:'1px solid rgba(255,255,255,.09)', background:amount===n?'rgba(0,212,255,.1)':'transparent', color:amount===n?'#00d4ff':'rgba(255,255,255,0.75)', cursor:'pointer', fontFamily:'monospace', fontSize:12, transition:'all .13s' }}>
            {n.toLocaleString()} {from}
          </button>
        ))}
      </div>

      <button onClick={fetchRates} style={{ marginTop:12, padding:'6px 14px', borderRadius:7, border:'1px solid rgba(255,255,255,.09)', background:'transparent', color:'rgba(255,255,255,0.75)', cursor:'pointer', fontSize:12, display:'inline-flex', alignItems:'center', gap:6 }}>
        ↺ Refresh rates
      </button>
    </div>
  );
}
